import { store } from '@/src/stores/store'
import type { TrackRegionConfig } from '../models/track/trackRegions'
import { gltfAsset } from '../assetStore'

export function getLaneCount() {
    return store.debugLaneCount
}

export const maxLifes = 5
export const hitReactionDuration = 0.8
export const obstacleCollisionHideDelay = 0.6
export const hitFreezeDuration = obstacleCollisionHideDelay

export const planePowerUpDuration = 5
export const shieldCollectCooldown = 1

export const levelSkyTransitionDuration = 2.5
export const skyThemeTransitionDuration = 16

export type SkyThemeConfig = {
    bottomColor: string
    baseColor: string
    highlightColor: string
    fogColor: string
}

export const skyRadius = 40
export const skySphereWidthSegments = 48
export const skySphereHeightSegments = 24
export const skyFocusDirection = [0, -0.25, -1] as const
export const skyBottomStartY = -0.3
export const skyBottomEndY = 0
export const skyGradientStart = 0.8
export const skyGradientEnd = 1
export const skyGradientPower = 0.8
export const skyDefaultBackgroundColor = '#70bfff'
export const skyDefaultBottomColor = '#0856c4'
export const skyDefaultBaseColor = '#70bfff'
export const skyDefaultHighlightColor = '#b8edff'
export const skyThemes: readonly SkyThemeConfig[] = [
    {
        bottomColor: '#1244af',
        baseColor: '#2469ff',
        highlightColor: '#d7e1fe',
        fogColor: '#5c85ff',
    },
    {
        bottomColor: '#1988c5',
        baseColor: '#499cc5',
        highlightColor: '#feda62',
        fogColor: '#499cc5',
    },
    {
        bottomColor: '#409adf',
        baseColor: '#8ad2ff',
        highlightColor: '#f5fbff',
        fogColor: '#ccebff',
    },
    {
        bottomColor: '#4f53d1',
        baseColor: '#001a66',
        highlightColor: '#4e43df',
        fogColor: '#000785',
    },
] as const

export const heightFogDefaultEnabled = true
export const heightFogDefaultColor = '#b3e2ff'
export const heightFogDefaultDensity = 0.04
export const heightFogDefaultHeight = -4.4
export const heightFogDefaultFalloff = 0.2
export const heightFogDefaultNear = 200
export const heightFogDefaultFar = 400

export const levelEndPassOffset = 2

export const cameraDampingHorizontal = 2
export const cameraDampingVertical = 2
export const cameraDampingRoll = 2

export const environmentSpacing = 10
export const environmentSideOffset = 6
export const environmentStartOffset = 10

export const tunnelExitRotationSpeed = 0.5

export const trackGeoStartLength = 1
export const trackStartAccelerationDuration = 1.5
export const trackEndDecelerationDuration = 2.0
export const trackRegionConfig: TrackRegionConfig = {
    transition1: { startPoint: 9, endPoint: 10 },
    transition2: { startPoint: 16, endPoint: 17 },
    transition3: { startPoint: 28, endPoint: 29 },
}

export const StartTrack = 1
export const laneSwitchDuration = 18
export const lanePositions = [0, -0.65, 0, 0.65]

export const obstacleScaleStartDistance = 90
export const obstacleScaleEndDistance = 12

export const obstacleModels = {
    level1: {
        type1: gltfAsset.Obstacle_LVL01_TYP01,
        type2: gltfAsset.Obstacle_LVL01_TYP02,
        type3: gltfAsset.Obstacle_LVL01_TYP03,
        type6: gltfAsset.Obstacle_LVL01_TYP06,
    },
    level2: {
        type1: gltfAsset.Obstacle_LVL02_TYP01,
        type2: gltfAsset.Obstacle_LVL02_TYP02,
        type4: gltfAsset.Obstacle_LVL02_TYP04,
        type5: gltfAsset.Obstacle_LVL02_TYP05,
    },
    level3: {
        type1: gltfAsset.Obstacle_LVL03_TYP01,
        type4: gltfAsset.Obstacle_LVL03_TYP04,
        type5: gltfAsset.Obstacle_LVL03_TYP05,
        type6: gltfAsset.Obstacle_LVL03_TYP06,
    },
    level4: {
        type3: gltfAsset.Obstacle_LVL04_TYP03,
        type5: gltfAsset.Obstacle_LVL04_TYP05,
        type6: gltfAsset.Obstacle_LVL04_TYP06,
        type7: gltfAsset.Obstacle_LVL04_TYP07,
    }
} as const

export const powerUpScaleStartDistance = 50
export const powerUpScaleEndDistance = 20
export const powerUpRaisedYOffset = 0.5

export const characterRotationDragSpeed = 0.01
export const characterRotationResetDuration = 2.0
export const characterLevelCompleteResetDuration = 2.0
export const characterWalkInDelay = 1.6
export const characterPreRunDefaultRotationY = Math.PI / 3

export const characterEndCardDuration = 2.0
export const characterEndCardHeight = 3.7
export const characterEndCardXOffset = 0.3
export const characterEndCardRotationY = Math.PI / 2
export const characterEndCardYOffset = -0.5

export const planePowerUpSpeedMultiplier = 1.4
export const batteryObstacleDestroyRange = 40

export const laneTurnAngle = Math.PI / 4
export const turnSmoothing = 18

export const jumpHeight = 0.9
export const jumpVisualHeightMultiplier = 0.5
export const jumpAnimationSpeedMultiplier = 1.2
export const jumpDuration = 0.868
export const jumpUnlockDuration = 0.7
// Actual jump animation in 3D file is 1.042 seconds
// Jump animation is divided by jumpAnimationSpeedMultiplier 1.042 / 1.2 = 0.868
// jumpDuration is extra jump animation in code and should match it otherwise looks strange
// So if longer or shorter jump is desired change both jumpAnimationSpeedMultiplier and jumpDuration
// jumpUnlockDuration unlocks another jump before animation has finished



export const tutorialHintStartPositions = {
    right: 30,
    left: 60,
    jump: 100,
    snap: 440,
} as const

export const tutorialHintEndPositions = {
    right: 50,
    left: 80,
    jump: 120,
    snap: 680,
} as const

export const runAnimationSpeedMin = 1.0
export const runAnimationSpeedMax = 2.0

export const endCardVerticalOffset = 2.8
export const endCardDepthOffset = 1.0
export const endCardBackgroundScreenScale = 0.4
export const endCardBackgroundOffsetX = 0
export const endCardBackgroundOffsetYA = 0
export const endCardBackgroundOffsetYB = 0.38
export const endCardBackgroundOffsetYBStele = 0.58
export const endCardBackgroundOffsetYTransitionDuration = 2
export const endCardStreaksSpeed = 0.5
export const endCardStreaksDensity = 0.8
export const endCardStreaksWidthCenter = 0.25
export const endCardStreaksWidthEdge = 0.2
export const endCardStreaksEdgeFadeStart = 0.2
export const endCardStreaksEdgeFadeEnd = 0.6
export const endCardStreaksEdgeNoise = 0.04
export const endCardStreaksGradientMid = 0.15
export const endCardStreaksGradientOuterStart = 0.3

export const level4StarsCenterX = 0
export const level4StarsCenterY = 0
export const level4StarsCenterZ = -2600
export const level4StarsHorizontalSpread = 2000
export const level4StarsHeightMin = -40
export const level4StarsHeightMax = 1100
export const level4StarsDepthNear = -1000
export const level4StarsDepthFar = -3000
export const level4StarsSizeMin = 4
export const level4StarsSizeMax = 24
export const level4StarsTwinkleMin = 1
export const level4StarsTwinkleMax = 3

export const tunnelShaderOffsetX = 0
export const tunnelShaderOffsetY = 0
export const tunnelShaderTextureRotationSpeed = -0.9
export const tunnelShaderRotationCenterY = 0.6
export const tunnelShaderTextureScale = 1.7

export const shieldBreakEffectColor = '#0055ff'
export const shieldBreakEffectCount = 20
export const shieldBreakEffectSize = 0.5
export const shieldBreakEffectEndSize = 1
export const shieldBreakEffectOpacity = 1
export const shieldBreakEffectLifetime = 1
export const shieldBreakEffectDrag = 9
export const shieldBreakEffectSpeed = 5
export const shieldBreakEffectUpwardDrift = 1.5
export const shieldBreakEffectOutwardVelocity = 0.7
export const shieldBreakEffectRandomVelocity = 2
export const shieldBreakEffectCenterY = 0.6
export const shieldBreakEffectHalfExtentsX = 0.3
export const shieldBreakEffectHalfExtentsY = 1.8
export const shieldBreakEffectHalfExtentsZ = 0.01

export const endCardSparkEmitter1X = 1.2
export const endCardSparkEmitter1Y = 1.96
export const endCardSparkEmitter1Z = 0.5
export const endCardSparkEmitter2X = -1
export const endCardSparkEmitter2Y = 1.96
export const endCardSparkEmitter2Z = -0.8
export const endCardSparkEmitter3X = 0.9
export const endCardSparkEmitter3Y = -1.244
export const endCardSparkEmitter3Z = 0.5
export const endCardSparkEmitter4X = 0.1
export const endCardSparkEmitter4Y = -1.244
export const endCardSparkEmitter4Z = -0.14
export const endCardSparkEmitter5X = -0.78
export const endCardSparkEmitter5Y = -1.244
export const endCardSparkEmitter5Z = -0.7
export const endCardSparkCount = 23
export const endCardSparkSize = 0.02
export const endCardSparkEndSize = 0.08
export const endCardSparkOpacity = 1
export const endCardSparkLifetime = 1.25
export const endCardSparkDrag = 6
export const endCardSparkSpeed = 3
export const endCardSparkUpwardVelocity = 1
export const endCardSparkOutwardVelocity = 0
export const endCardSparkRandomVelocity = 1
export const endCardSparkPositionJitter = 0.2
export const endCardSparkBurstDelay = 0.27
export const endCardSparkShowEmitterDebug = false

export const heightFogEnabled = true
export const heightFogDensity = 0.04
export const heightFogHeight = -4.4
export const heightFogFalloff = 0.2
export const heightFogNear = 200
export const heightFogFar = 400
export const endCardTiltHorizontalNormal = 0.8
export const endCardTiltHorizontalStele = 0.9
export const endCardTiltVerticalNormal = 0.8
export const endCardTiltVerticalStele = 0.8
export const endCardTiltSpeedNormal = 0.3
export const endCardTiltSpeedStele = 0.2

export const characterOutlineThickness = 0.002
export const characterOutlineColor = '#000000'
export const characterOutlineTransitionDuration = 0.06
export const characterOutlineAdditive = false
export const characterOutlineGlow = 0
export const characterOutlinePulsePeriod = 0
export const characterOutlineSoftness = 1
export const characterOutlineStrength = 12
export const characterOutlineThicknessMobile = 0.002
export const characterOutlineAdditiveMobile = false
export const characterOutlineGlowMobile = 0
export const characterOutlinePulsePeriodMobile = 0
export const characterOutlineSoftnessMobile = 1
export const characterOutlineStrengthMobile = 12

export const characterOutlineCollectThickness = 0.01
export const characterOutlineCollectCoinColor = '#ffc800'
export const characterOutlineCollectHeartColor = '#d84113'
export const characterOutlineCollectShieldColor = '#0008ff'
export const characterOutlineCollectAdditive = false
export const characterOutlineCollectGlow = 1.5
export const characterOutlineCollectPulsePeriod = 0
export const characterOutlineCollectSoftness = 2
export const characterOutlineCollectStrength = 5
export const characterOutlineCollectThicknessMobile = 0.0075
export const characterOutlineCollectAdditiveMobile = false
export const characterOutlineCollectGlowMobile = 1.2
export const characterOutlineCollectPulsePeriodMobile = 0
export const characterOutlineCollectSoftnessMobile = 1
export const characterOutlineCollectStrengthMobile = 4.5

export const chameleonFresnelPower = 2.2
export const chameleonFresnelRimStart = 0.4
export const chameleonFresnelRimEnd = 0

export const effectPreviewDistance = 1.8
export const effectPreviewDuration = 3
export const effectPreviewHeight = -0.4
